"use client";

import { useState } from "react";
import { dispatchWorkflow } from "@/lib/queries";

// Fire-and-forget triggers for the two GitHub Actions workflows.
// GitHub's workflow_dispatch endpoint returns 204 with no run id, so
// there's genuinely nothing to poll for without a second lookup --
// hence the link out to the Actions tab rather than in-app progress
// (per user decision; can revisit if it ever becomes worth it).
export function PipelineSection() {
  const [pending, setPending] = useState<"full" | "reseed" | null>(null);
  const [result, setResult] = useState<{ workflow: string; actionsUrl: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (workflow: "full" | "reseed", confirmMessage: string) => {
    if (!window.confirm(confirmMessage)) return;
    setPending(workflow);
    setError(null);
    setResult(null);
    try {
      const data = await dispatchWorkflow(workflow);
      setResult({ workflow: data.workflow, actionsUrl: data.actionsUrl });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(null);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Pipeline</h1>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        Trigger a pipeline run without leaving the app. Runs happen on GitHub Actions — use the link
        after triggering to watch progress.
      </p>

      {error && (
        <div
          className="mt-4 rounded-md border px-4 py-3 text-sm"
          style={{
            borderColor: "var(--color-negative)",
            backgroundColor: "var(--color-negative-muted)",
            color: "var(--color-negative)",
          }}
        >
          {error}
        </div>
      )}

      {result && (
        <div
          className="mt-4 rounded-md border px-4 py-3 text-sm"
          style={{ borderColor: "var(--color-accent)", color: "var(--color-text-primary)" }}
        >
          Triggered <span className="font-medium">{result.workflow}</span>.{" "}
          <a
            href={result.actionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
            style={{ color: "var(--color-accent-strong)" }}
          >
            View progress on GitHub →
          </a>
          <div className="mt-1 text-xs text-[var(--color-text-tertiary)]">
            It can take a few seconds for the run to appear.
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-1)] p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium text-[var(--color-text-primary)]">Reseed only</div>
              <div className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Recomputes listing analysis, default offers, and projections from what&apos;s already
                in the database. No scraping. Use this after changing a global default or deploying
                projection logic — listings themselves won&apos;t change.
              </div>
            </div>
            <button
              onClick={() =>
                run(
                  "reseed",
                  "Run reseed? This recomputes analysis, default offers, and projections. It does not scrape."
                )
              }
              disabled={pending !== null}
              className="shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              style={{ borderColor: "var(--color-accent)", color: "var(--color-accent-strong)" }}
            >
              {pending === "reseed" ? "Triggering…" : "Run reseed"}
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-1)] p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium text-[var(--color-text-primary)]">
                Full pipeline
              </div>
              <div className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Scrapes both sources, ingests new and sold listings, then does everything reseed
                does. Slower, and the one to use when you actually want a fresh listing set. This
                also runs automatically once a day.
              </div>
            </div>
            <button
              onClick={() =>
                run(
                  "full",
                  "Run the FULL pipeline? This scrapes both sources and can mark missing listings as sold."
                )
              }
              disabled={pending !== null}
              className="shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
            >
              {pending === "full" ? "Triggering…" : "Run full pipeline"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
